import { DEFAULT_LOCALE, isLocale, type Locale } from '@/router/locales'

type RecordLike = Record<string, unknown>

export type NoticeVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export type RawNotice = {
  id?: number | string
  slug?: string
  title?: unknown
  message?: unknown
  content?: unknown
  description?: unknown
  variant?: unknown
  type?: unknown
  status?: unknown
  icon?: unknown
  dismissible?: unknown
  alert?: unknown
  page_ids?: unknown
  pageIds?: unknown
  pages?: unknown
  locale?: unknown
  lang?: unknown
  active?: unknown
  enabled?: unknown
  starts_at?: unknown
  ends_at?: unknown
}

export type NoticeModel = {
  id: string
  title?: string
  message: string
  variant: NoticeVariant
  icon?: string
  dismissible: boolean
  alert: boolean
  pageIds: number[]
  locale?: Locale
  startsAt?: string
  endsAt?: string
}

function toRecord(value: unknown): RecordLike {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : {}
}

function textFromRendered(value: unknown): string | undefined {
  const record = toRecord(value)
  const rendered = record.rendered

  return typeof rendered === 'string' ? rendered.trim() || undefined : undefined
}

function localizedText(value: unknown, locale: Locale): string | undefined {
  if (typeof value === 'string') {
    return value.trim() || undefined
  }

  const rendered = textFromRendered(value)
  if (rendered) return rendered

  const record = toRecord(value)
  const localized = record[locale] ?? record[DEFAULT_LOCALE] ?? record.en

  if (typeof localized === 'string') {
    return localized.trim() || undefined
  }

  return textFromRendered(localized)
}

function normalizeVariant(value: unknown): NoticeVariant {
  const raw = typeof value === 'string' ? value.toLowerCase().trim() : ''

  switch (raw) {
    case 'error':
    case 'danger':
      return 'danger'
    case 'warn':
    case 'warning':
      return 'warning'
    case 'success':
      return 'success'
    case 'info':
      return 'info'
    default:
      return 'neutral'
  }
}

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim()
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false
  }

  return fallback
}

function normalizePageIds(value: unknown): number[] {
  const values = Array.isArray(value) ? value : [value]

  return values
    .map((item) => {
      if (typeof item === 'number') return item
      if (typeof item === 'string') return Number.parseInt(item, 10)

      const record = toRecord(item)
      const id = record.ID ?? record.id

      if (typeof id === 'number') return id
      if (typeof id === 'string') return Number.parseInt(id, 10)

      return Number.NaN
    })
    .filter((item): item is number => Number.isFinite(item) && item > 0)
}

function normalizeLocale(value: unknown): Locale | undefined {
  return typeof value === 'string' && isLocale(value) ? value : undefined
}

function isActive(raw: RawNotice) {
  return normalizeBoolean(raw.active ?? raw.enabled, true)
}

export function notice(raw: RawNotice, locale: Locale): NoticeModel | null {
  if (!isActive(raw)) return null

  const message = localizedText(raw.message ?? raw.content ?? raw.description, locale)
  if (!message) return null

  const id = String(raw.id ?? raw.slug ?? message).trim()

  return {
    id,
    title: localizedText(raw.title, locale),
    message,
    variant: normalizeVariant(raw.variant ?? raw.type ?? raw.status),
    icon: typeof raw.icon === 'string' ? raw.icon.trim() || undefined : undefined,
    dismissible: normalizeBoolean(raw.dismissible, true),
    alert: normalizeBoolean(raw.alert, true),
    pageIds: normalizePageIds(raw.page_ids ?? raw.pageIds ?? raw.pages),
    locale: normalizeLocale(raw.locale ?? raw.lang),
    startsAt: typeof raw.starts_at === 'string' ? raw.starts_at : undefined,
    endsAt: typeof raw.ends_at === 'string' ? raw.ends_at : undefined,
  }
}
